/**
 * Spelling Challenge Game
 * Listen and spell the word correctly
 */
import { useState, useEffect } from 'react';
import { VocabularyWord } from '../../data/vocabulary';
import { mistakeReviewService } from '../../services/mistake-review';

interface SpellingGameProps {
  words: VocabularyWord[];
  onComplete: (score: number) => void;
  onExit: () => void;
}

export default function SpellingGame({ words, onComplete, onExit }: SpellingGameProps) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [input, setInput] = useState('');
  const [score, setScore] = useState(0);
  const [feedback, setFeedback] = useState<'correct' | 'wrong' | null>(null);
  const [showHint, setShowHint] = useState(false);
  const [hintsUsed, setHintsUsed] = useState(0);

  const currentWord = words[currentIndex];

  useEffect(() => {
    setInput('');
    setFeedback(null);
    setShowHint(false);
  }, [currentIndex]);

  // Speak the word
  const speakWord = () => {
    if (currentWord) {
      const utterance = new SpeechSynthesisUtterance(currentWord.word);
      utterance.lang = 'en-US';
      utterance.rate = 0.7; // Slow speed for kids
      speechSynthesis.speak(utterance);
    }
  };

  // Auto speak when word changes
  useEffect(() => {
    if (currentWord) {
      setTimeout(() => speakWord(), 500);
    }
  }, [currentIndex]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!input.trim()) return;

    const correct = input.trim().toLowerCase() === currentWord.word.toLowerCase();

    if (correct) {
      setFeedback('correct');
      const baseScore = 15;
      const hintPenalty = hintsUsed * 3;
      const finalPoints = Math.max(baseScore - hintPenalty, 5);
      setScore(s => s + finalPoints);

      setTimeout(() => {
        if (currentIndex < words.length - 1) {
          setCurrentIndex(i => i + 1);
          setHintsUsed(0);
        } else {
          onComplete(score + finalPoints);
        }
      }, 1500);
    } else {
      setFeedback('wrong');

      // Record spelling mistake
      if (mistakeReviewService) {
        mistakeReviewService.addMistake(
          'vocabulary',
          currentWord.id,
          currentWord.word,
          input.trim(),
          currentWord.word
        );
      }

      setTimeout(() => setFeedback(null), 2000);
    }
  };

  const handleHint = () => {
    setShowHint(true);
    setHintsUsed(hintsUsed + 1);
  };

  const allDone = currentIndex === words.length - 1 && feedback === 'correct';

  if (!currentWord) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-400">暂无词汇数据</p>
        <button onClick={onExit} className="mt-4 text-green-500">返回</button>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-lg mx-auto">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <button onClick={onExit} className="text-gray-500 hover:text-gray-700">
          ← 返回
        </button>
        <div className="text-center">
          <h2 className="text-xl font-bold text-green-600">📝 拼写挑战</h2>
          <p className="text-sm text-gray-500">听音拼写单词</p>
        </div>
        <div className="text-right">
          <p className="text-sm font-bold text-green-600">得分: {score}</p>
          <p className="text-xs text-gray-400">第 {currentIndex + 1}/{words.length} 题</p>
        </div>
      </div>

      {/* Word image */}
      <div className="text-center mb-6">
        {currentWord.image && (
          <div className="text-6xl mb-4 animate-bounce">{currentWord.image}</div>
        )}
        <p className="text-gray-500 text-sm">听发音，拼写出这个单词</p>
      </div>

      {/* Listen button */}
      <div className="flex justify-center mb-6">
        <button
          onClick={speakWord}
          className="px-8 py-4 bg-green-100 text-green-700 rounded-2xl text-lg font-bold hover:bg-green-200 animate-pulse"
        >
          🔊 再听一遍
        </button>
      </div>

      {/* Hint */}
      {!showHint && (
        <div className="text-center mb-4">
          <button
            onClick={handleHint}
            className="text-sm text-gray-400 hover:text-gray-600"
          >
            💡 需要提示吗？（会扣分）
          </button>
        </div>
      )}

      {showHint && (
        <div className="bg-yellow-50 rounded-xl p-3 mb-4 text-center animate-bounce-in">
          <p className="text-sm text-gray-600">
            提示：单词有 {currentWord.word.length} 个字母
            {currentWord.word.length > 3 && (
              <span>，首字母是 "{currentWord.word[0].toUpperCase()}"</span>
            )}
          </p>
        </div>
      )}

      {/* Input form */}
      <form onSubmit={handleSubmit} className="mb-6">
        <input
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder="输入单词..."
          disabled={!!feedback}
          className="w-full px-4 py-3 text-xl text-center border-2 border-gray-200 rounded-xl focus:border-green-400 focus:outline-none disabled:opacity-50"
          autoFocus
        />
      </form>

      {/* Submit button */}
      <div className="flex justify-center">
        <button
          onClick={handleSubmit}
          disabled={!input.trim() || !!feedback}
          className="px-6 py-3 bg-green-500 text-white rounded-xl font-bold hover:bg-green-600 disabled:opacity-50"
        >
          ✓ 提交答案
        </button>
      </div>

      {/* Feedback */}
      {feedback && (
        <div className={`mt-6 text-center animate-bounce-in ${
          feedback === 'correct' ? 'text-green-600' : 'text-red-500'
        }`}>
          <div className="text-lg font-bold mb-2">
            {feedback === 'correct' ? '✅ 正确！' : '❌ 再试试'}
          </div>
          {feedback === 'wrong' && (
            <div className="text-gray-600">
              <p className="mb-1">正确拼写：</p>
              <p className="text-xl font-bold text-gray-800">{currentWord.word}</p>
              <p className="text-sm text-gray-500 mt-1">{currentWord.meaning}</p>
            </div>
          )}
        </div>
      )}

      {/* Completion */}
      {allDone && (
        <div className="fixed inset-0 bg-white/90 flex items-center justify-center animate-bounce-in">
          <div className="text-center">
            <div className="text-6xl mb-4">🏆</div>
            <h2 className="text-2xl font-bold text-green-600 mb-2">拼写大师！</h2>
            <p className="text-lg text-gray-600">总分: {score} 分</p>
            <button
              onClick={onExit}
              className="mt-6 px-6 py-3 bg-green-500 text-white rounded-xl font-bold hover:bg-green-600"
            >
              完成 ✓
            </button>
          </div>
        </div>
      )}
    </div>
  );
}